import React, { useState, useEffect } from "react";
import { Card, Space, Tag, Typography, Button, Tooltip } from "antd";
import {
  PaperClipOutlined,
  FileTextOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { getContainerDocuments } from "../../services/containerDocumentsService";
import { getMasterDocumentForContainer } from "../../services/masterContainerDocumentsService";
import { ContainerDocumentType } from "../../utils/containerDocumentsUtils";

const { Text } = Typography;

interface ContainerDocumentsSummaryProps {
  containerId: number;
  onScrollToDocuments: () => void;
}

/**
 * Get document type display name
 */
const getDocumentTypeName = (type: ContainerDocumentType): string => {
  const names: Record<ContainerDocumentType, string> = {
    [ContainerDocumentType.AWB_BL]: "AWB/BL",
    [ContainerDocumentType.VRIJSTELLING]: "Vrijstelling",
    [ContainerDocumentType.T1_DOCUMENT]: "T1 Document",
  };
  return names[type] || type;
};

const ContainerDocumentsSummary: React.FC<ContainerDocumentsSummaryProps> = ({
  containerId,
  onScrollToDocuments,
}) => {
  const [documentCounts, setDocumentCounts] = useState<
    Record<ContainerDocumentType, number>
  >({
    [ContainerDocumentType.AWB_BL]: 0,
    [ContainerDocumentType.VRIJSTELLING]: 0,
    [ContainerDocumentType.T1_DOCUMENT]: 0,
  });
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Function to fetch and update counts
  const fetchCounts = React.useCallback(async () => {
    try {
      setLoading(true);
      const [containerResult, masterDocResult] = await Promise.all([
        getContainerDocuments(containerId),
        getMasterDocumentForContainer(containerId),
      ]);

      if (containerResult.error || !containerResult.success) {
        return;
      }

      const counts: Record<ContainerDocumentType, number> = {
        [ContainerDocumentType.AWB_BL]: masterDocResult.data ? 1 : 0,
        [ContainerDocumentType.VRIJSTELLING]: 0,
        [ContainerDocumentType.T1_DOCUMENT]: 0,
      };

      let total = counts[ContainerDocumentType.AWB_BL];
      containerResult.data.forEach((doc) => {
        if (doc.document_type === ContainerDocumentType.AWB_BL) return;
        counts[doc.document_type]++;
        total++;
      });

      setDocumentCounts(counts);
      setTotalCount(total);
    } catch (error) {
      console.error("Error fetching document counts:", error);
    } finally {
      setLoading(false);
    }
  }, [containerId]);

  // Initial fetch
  useEffect(() => {
    if (containerId) {
      fetchCounts();
    }
  }, [containerId, fetchCounts]);

  // Expose refresh function via window for ContainerDocuments to call
  useEffect(() => {
    // Store refresh function in a way ContainerDocuments can access it
    (window as any)[`container_${containerId}_refresh_docs`] = fetchCounts;
    return () => {
      delete (window as any)[`container_${containerId}_refresh_docs`];
    };
  }, [containerId, fetchCounts]);

  if (loading) {
    return null; // Don't show anything while loading
  }

  return (
    <Card
      size="small"
      style={{
        marginTop: 16,
        marginBottom: 0,
        cursor: "pointer",
        transition: "all 0.2s",
      }}
      hoverable
      onClick={onScrollToDocuments}
      bodyStyle={{ padding: "8px 16px" }}
    >
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        size="small"
      >
        <Space size="middle">
          <PaperClipOutlined style={{ color: "#1890ff" }} />
          <Text strong style={{ fontSize: 13 }}>
            Documents
          </Text>
          <Space size={8}>
            {Object.entries(documentCounts).map(([type, count]) => {
              if (count === 0) return null;
              return (
                <Tooltip
                  key={type}
                  title={`${getDocumentTypeName(type as ContainerDocumentType)}: ${count} file${count > 1 ? "s" : ""}`}
                >
                  <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                    {getDocumentTypeName(type as ContainerDocumentType)}: {count}
                  </Tag>
                </Tooltip>
              );
            })}
            {totalCount === 0 && (
              <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                No documents
              </Tag>
            )}
          </Space>
        </Space>
        <Space size="small">
          {totalCount > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {totalCount} file{totalCount > 1 ? "s" : ""} total
            </Text>
          )}
          <Button
            type="link"
            size="small"
            icon={<DownOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onScrollToDocuments();
            }}
            style={{ padding: 0, height: "auto", fontSize: 12 }}
          >
            View all
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

export default ContainerDocumentsSummary;

